import type { Edge, Node } from '@xyflow/react';
import { VALID_ACTION_TYPES } from './workflowConstants';
import { inferCanvasNodeType, normalizeCanvasNodeType } from './workflowGraphAnalysis';

export interface WorkflowNormalizationIssue {
  type: 'invalid-node' | 'unsupported-node' | 'invalid-edge';
  message: string;
  nodeId?: string;
  edgeId?: string;
}

export interface NormalizedWorkflowGraph {
  nodes: Node[];
  edges: Edge[];
  issues: WorkflowNormalizationIssue[];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function normalizePosition(rawPosition: unknown): { x: number; y: number } {
  const position = asRecord(rawPosition);
  const x = typeof position?.x === 'number' ? position.x : 0;
  const y = typeof position?.y === 'number' ? position.y : 0;
  return { x, y };
}

function normalizeClickType(rawClickType: unknown): 'left' | 'right' | 'double' {
  return rawClickType === 'right' || rawClickType === 'double' ? rawClickType : 'left';
}

function normalizeWaitType(rawWaitType: unknown): 'time' | 'element-appear' | 'element-disappear' {
  return rawWaitType === 'element-appear' || rawWaitType === 'element-disappear'
    ? rawWaitType
    : 'time';
}

function normalizeActionConfig(
  actionType: string,
  label: string,
  rawConfig: Record<string, unknown>
): Record<string, unknown> {
  const config: Record<string, unknown> = {
    ...rawConfig,
    type: actionType,
    label,
  };

  switch (actionType) {
    case 'click':
      config.clickType = normalizeClickType(config.clickType);
      break;
    case 'type':
      config.clearBefore = config.clearBefore !== false;
      delete config.humanLike;
      delete config.speed;
      break;
    case 'wait':
      config.waitType = normalizeWaitType(config.waitType);
      break;
    case 'navigate':
      delete config.browser;
      delete config.waitForLoad;
      break;
    case 'open-app':
      config.timeout = typeof config.timeout === 'number' ? config.timeout : 30;
      config.useExisting = config.useExisting === true;
      break;
    case 'select-grid-row':
      config.clickType = config.clickType || 'double';
      config.matchMode = config.matchMode || 'contains';
      break;
    default:
      break;
  }

  return config;
}

function normalizeExecutableNode(
  nodeId: string,
  nodeType: 'action' | 'loop' | 'if-else',
  rawNode: Record<string, unknown>,
  rawData: Record<string, unknown>,
  rawConfig: Record<string, unknown>
): Node {
  const inferredConfigType =
    typeof rawConfig.type === 'string'
      ? rawConfig.type
      : typeof rawData.type === 'string'
        ? rawData.type
        : nodeType;
  const label =
    typeof rawConfig.label === 'string'
      ? rawConfig.label
      : typeof rawData.label === 'string'
        ? rawData.label
        : `Nodo ${nodeId}`;
  const normalizedConfig =
    nodeType === 'action'
      ? normalizeActionConfig(inferredConfigType, label, rawConfig)
      : {
          ...rawConfig,
          type: nodeType,
          label,
        };

  const normalizedData = {
    ...rawData,
    label,
    type: normalizedConfig.type,
    config: normalizedConfig,
  };

  // H-F018: El cast a Node es seguro aquí porque los campos críticos (id, type,
  // position, data) se reemplazan explícitamente con valores validados.
  // El spread de rawNode solo aporta campos opcionales de ReactFlow (selected, etc.)
  const normalizedNode = {
    ...(rawNode as unknown as Node),
    id: nodeId,
    type: nodeType,
    position: normalizePosition(rawNode.position),
    data: normalizedData,
  } as Node;

  if (typeof rawNode.parentId === 'string') {
    normalizedNode.parentId = rawNode.parentId;
  }

  return normalizedNode;
}

function normalizeNoteNode(
  nodeId: string,
  rawNode: Record<string, unknown>,
  rawData: Record<string, unknown> | null
): Node {
  // H-F018: Cast seguro — id, type, position y data se establecen explícitamente
  return {
    ...(rawNode as unknown as Node),
    id: nodeId,
    type: 'note',
    position: normalizePosition(rawNode.position),
    data: rawData ?? {
      title: 'Nota',
      content: '',
      color: 'yellow',
    },
  };
}

function normalizeStartNode(
  nodeId: string,
  rawNode: Record<string, unknown>
): Node {
  return {
    ...(rawNode as unknown as Node),
    id: nodeId,
    type: 'start',
    deletable: false,
    position: normalizePosition(rawNode.position),
    data: {},
  };
}

export function normalizeWorkflowGraph(rawNodes: unknown[], rawEdges: unknown[]): NormalizedWorkflowGraph {
  const issues: WorkflowNormalizationIssue[] = [];
  const nodes: Node[] = [];

  for (const rawNodeValue of rawNodes) {
    const rawNode = asRecord(rawNodeValue);
    if (!rawNode) {
      issues.push({
        type: 'invalid-node',
        message: 'Uno o mas nodos no tienen formato de objeto',
      });
      continue;
    }

    if (typeof rawNode.id !== 'string' || !rawNode.id.trim()) {
      issues.push({
        type: 'invalid-node',
        message: 'Uno o mas nodos no tienen un id valido',
      });
      continue;
    }

    const rawData = asRecord(rawNode.data);
    const rawConfig = asRecord(rawData?.config) ?? {};
    const normalizedNodeType =
      normalizeCanvasNodeType(rawNode.type) ??
      inferCanvasNodeType(rawData?.type, rawConfig.type);

    if (!normalizedNodeType) {
      issues.push({
        type: 'unsupported-node',
        nodeId: rawNode.id,
        message: `El nodo ${rawNode.id} usa un type no soportado`,
      });
      continue;
    }

    if (normalizedNodeType === 'note') {
      nodes.push(normalizeNoteNode(rawNode.id, rawNode, rawData));
      continue;
    }

    if (normalizedNodeType === 'start') {
      nodes.push(normalizeStartNode(rawNode.id, rawNode));
      continue;
    }

    const inferredConfigType =
      typeof rawConfig.type === 'string'
        ? rawConfig.type
        : typeof rawData?.type === 'string'
          ? rawData.type
          : normalizedNodeType;

    if (
      normalizedNodeType === 'action' &&
      !(VALID_ACTION_TYPES as readonly string[]).includes(inferredConfigType)
    ) {
      issues.push({
        type: 'unsupported-node',
        nodeId: rawNode.id,
        message: `El nodo ${rawNode.id} usa una accion no soportada: ${String(inferredConfigType)}`,
      });
      continue;
    }

    nodes.push(
      normalizeExecutableNode(
        rawNode.id,
        normalizedNodeType,
        rawNode,
        rawData ?? {},
        rawConfig
      )
    );
  }

  const edges: Edge[] = [];
  for (const rawEdgeValue of rawEdges) {
    const rawEdge = asRecord(rawEdgeValue);
    if (!rawEdge) {
      issues.push({
        type: 'invalid-edge',
        message: 'Uno o mas edges no tienen formato de objeto',
      });
      continue;
    }

    if (typeof rawEdge.source !== 'string' || typeof rawEdge.target !== 'string') {
      issues.push({
        type: 'invalid-edge',
        edgeId: typeof rawEdge.id === 'string' ? rawEdge.id : undefined,
        message: 'Uno o mas edges no tienen source/target validos',
      });
      continue;
    }

    edges.push({
      ...(rawEdge as unknown as Edge),
      id: typeof rawEdge.id === 'string' ? rawEdge.id : `e-${rawEdge.source}-${rawEdge.target}`,
      source: rawEdge.source,
      target: rawEdge.target,
      sourceHandle: typeof rawEdge.sourceHandle === 'string' ? rawEdge.sourceHandle : undefined,
      targetHandle: typeof rawEdge.targetHandle === 'string' ? rawEdge.targetHandle : undefined,
    });
  }

  return { nodes, edges, issues };
}
